<template>
  <div
    class="dashboard-proposal"
  >
    <div
      v-if="officeOffer.cover"
      class="dashboard-proposal__cover"
    >
      <media-image
        v-if="officeOffer.cover"
        :uuid="officeOffer.cover.uuid"
        :alt="officeOffer.cover.description"
        :ratio="[ 294, 70 ]"
      />
      <img
        v-else
        :data-src="`${cdn_url}/images/1x1-grey.png`"
        :src="`${cdn_url}/images/1x1-grey.png`"
        alt="default"
      >
    </div>

    <div class="dashboard-proposal__office-offer">
      <div class="dp-oo__address">
        {{ officeOffer.address.street }}, {{ zipCodeBeautify(officeOffer.address.zip_code) }}
      </div>
      <div v-if="proposal.office_offers?.length > 1">
        <div class="dp-oo__text">
          {{ trans('offer_card.display_types.multioffice', { 'count': proposal.office_offers?.length }) }}
        </div>
        <div class="dp-oo__text">
          {{ displayMultiOfficeCapacities }} {{ trans('global.post_office.plural') }}
        </div>
        <div class="dp-oo__text">
          {{ currencyNoDecimal(displayMultiOfficeCumulativePrice) }} {{ trans('global.price_tax') }} / {{ trans('global.duration.month') }}
        </div>
      </div>
      <div v-else-if="isOpenSpace">
        <div class="dp-oo__text">
          {{ officeOffer.office_type.name }} &bull;
          {{ transchoice('deal_dashboard.card.offer.seats_count', { count: officeOffer.capacity }) }}
        </div>
        <div class="dp-oo__text">
          {{ currency(officeOffer.price) }} {{ trans('global.price_tax') }} /
          {{ trans('deal_dashboard.card.offer.seat') }} /
          {{ trans('global.duration.month') }}
        </div>
      </div>
      <div v-else-if="isPrivateOrIndependent">
        <div class="dp-oo__text">
          {{ officeOffer.office_type.name }} &bull;
          {{ officeOffer.surface }}m²
        </div>
        <div class="dp-oo__text">
          {{ currency(officeOffer.price) }} {{ trans('global.price_tax') }} /
          {{ trans('global.duration.month') }}
        </div>
      </div>
    </div>

    <div class="dashboard-proposal__account-manager">
      <div
        :class="{ 'dp-am__info': true, 'dp-am__info--with-message': hasMessage }"
        @click="showMessage = !showMessage"
      >
        <ProfilePicture
          v-if="accountManager && accountManager.picture_url"
          :url="accountManager.picture_url"
          :user-id="accountManager.id"
          :user-firstname="null"
          class="dp-am-i__profile-picture"
        />
        <div
          v-if="hasMessage"
          class="dp-am-i__message-cta"
        >
          <span>{{ trans('deal_dashboard.proposal.message_from') }}</span>
          <span :class="{ 'caret-down': true, 'opened': showMessage }" />
        </div>
        <div v-else>
          {{ trans('deal_dashboard.proposal.recommended_by') }}
        </div>
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="hasMessage && showMessage"
        class="dp-am__message"
        v-html="proposal.proposal_comment"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div class="dp-am__ctas">
        <button
          :class="buttonRefuseClassnames"
          @click="onRefuseClick"
        >
          {{ trans('deal_dashboard.proposal.cta.refuse') }}
        </button>
        <ContactOfferButton
          :hash-ids="[ proposal.office_offer.hash_id ]"
          :proposal-id="proposal.id"
        >
          {{ trans('deal_dashboard.proposal.cta.contact') }}
        </ContactOfferButton>
      </div>
    </div>
  </div>
</template>

<script>
import ContactOfferButton from '@components/ContactOfferButton.vue';
import MediaImage from '@components/MediaImage.vue';
import ProfilePicture from '@components/ProfilePicture.vue';
import { translationMixin } from '@elements/translation';
import { currency, currencyNoDecimal } from '@elements/number-formatter';
import { beautify as zipCodeBeautify } from '@molecules/zip-code-formatter';
import { eventBus } from '@tools/event-bus';
import { requestsMixin } from '@tools/requests-mixin';
import { Slider } from '@enums/event';
import { OfficeTypeEnum } from '@organisms/offer/office-type-enum';
import { OFFER_EVENT } from '@organisms/offer/constants';

export default {
  components: { ContactOfferButton, MediaImage, ProfilePicture },
  mixins: [ translationMixin, requestsMixin ],
  props: {
    proposal: { type: Object, required: true },
  },
  data() {
    return {
      showMessage: false,
      refuseButtonLoading: false,
      cdn_url: window.CDN_URL,
    };
  },
  computed: {
    accountManager() {
      return this.proposal.account_manager;
    },
    officeOffer() {
      return this.proposal.office_offer;
    },
    isOpenSpace() {
      return this.officeOffer.office_type.code === OfficeTypeEnum.OPEN_SPACE;
    },
    isPrivateOrIndependent() {
      return [ OfficeTypeEnum.PRIVATE, OfficeTypeEnum.INDEPENDENT ].includes(this.officeOffer.office_type.code);
    },
    hasMessage() {
      return this.proposal.proposal_comment.length > 0;
    },
    buttonRefuseClassnames() {
      return {
        btn: true,
        'btn--secondary': true,
        'btn--refuse': true,
        'loading-link': this.refuseButtonLoading,
      };
    },
    displayMultiOfficeCapacities() {
      return this.proposal.office_offers_capacities;
    },
    displayMultiOfficeCumulativePrice() {
      return this.proposal.office_offers.reduce((total, officeOffer) => total + officeOffer.price, 0);
    },
  },
  mounted() {
    eventBus.addEventListener(OFFER_EVENT.PROPOSAL_REFUSE, this.proposalRemoved, { detail: { proposalId: this.proposal.id } });
  },
  methods: {
    currencyNoDecimal,
    zipCodeBeautify,
    currency,
    proposalRemoved(event) {
      const { proposalId } = event.detail;
      if (proposalId === this.proposal.id) {
        this.$emit('proposal-refused', this.proposal);
      }
    },
    async onRefuseClick() {
      eventBus.dispatchEvent(
        new CustomEvent('slider-show', {
          detail: {
            sliderType: Slider.REFUSE_SELECTION_ITEM,
            sliderPayload: { id: this.proposal.id },
          },
        }),
      );
    },
  },
};
</script>
